export const REPORT_SIDEBAR_CHANNEL = 'report-editor-sync';
export const REPORT_SIDEBAR_WINDOW_NAME = 'report-sidebar';
export const REPORT_SIDEBAR_LOGOUT_KEY = 'report-sidebar-logout-signal';

export const SYNC_MESSAGE = {
  CONTEXT_UPDATE: 'CONTEXT_UPDATE',
  CONTEXT_CLEAR: 'CONTEXT_CLEAR',
  DOCK: 'DOCK',
  DETACH: 'DETACH',
  POPUP_GEOMETRY: 'POPUP_GEOMETRY',
  POPUP_CLOSED: 'POPUP_CLOSED',
};

export const DEFAULT_POPUP_GEOMETRY = {
  left: null,
  top: 100,
  width: 420,
  height: 900,
};

export const DEFAULT_LAYOUT_SETTINGS = {
  leftColumnWidthPercent: 25,
  priorStudiesHeightPercent: 65,
};

export const LAYOUT_CONSTRAINTS = {
  leftColumnWidthPercent: { min: 18, max: 45 },
  priorStudiesHeightPercent: { min: 25, max: 85 },
};

export const DEFAULT_REPORT_EDITOR_SETTINGS = {
  sidebarDetached: false,
  popupWindow: { ...DEFAULT_POPUP_GEOMETRY },
  mainWindow: null,
  layout: { ...DEFAULT_LAYOUT_SETTINGS },
};

function clampLayoutValue(key, value) {
  const constraints = LAYOUT_CONSTRAINTS[key];
  if (!constraints || typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_LAYOUT_SETTINGS[key];
  }
  return Math.min(constraints.max, Math.max(constraints.min, Math.round(value)));
}

export function normalizeLayoutSettings(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_LAYOUT_SETTINGS };
  }
  return {
    leftColumnWidthPercent: clampLayoutValue(
      'leftColumnWidthPercent',
      raw.leftColumnWidthPercent
    ),
    priorStudiesHeightPercent: clampLayoutValue(
      'priorStudiesHeightPercent',
      raw.priorStudiesHeightPercent
    ),
  };
}

export function normalizeReportEditorSettings(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_REPORT_EDITOR_SETTINGS, popupWindow: { ...DEFAULT_POPUP_GEOMETRY } };
  }
  return {
    sidebarDetached: Boolean(raw.sidebarDetached),
    popupWindow: { ...DEFAULT_POPUP_GEOMETRY, ...(raw.popupWindow || {}) },
    mainWindow: raw.mainWindow || null,
    layout: normalizeLayoutSettings(raw.layout),
  };
}

export function getPopupFeatures(geometry) {
  const g = { ...DEFAULT_POPUP_GEOMETRY, ...(geometry || {}) };
  const width = g.width || DEFAULT_POPUP_GEOMETRY.width;
  const height = g.height || DEFAULT_POPUP_GEOMETRY.height;
  const left = g.left != null
    ? g.left
    : Math.max(0, Math.floor((window.screen.availWidth - width) / 2) + (window.screen.availLeft || 0));
  const top = g.top != null
    ? g.top
    : Math.max(0, Math.floor((window.screen.availHeight - height) / 4) + (window.screen.availTop || 0));

  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'resizable=yes',
    'scrollbars=yes',
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
  ].join(',');
}

export function readWindowGeometry(win) {
  if (!win || win.closed) return null;
  return {
    left: win.screenX,
    top: win.screenY,
    width: win.outerWidth,
    height: win.outerHeight,
  };
}

let channelInstance = null;

export function getReportSidebarChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channelInstance) {
    channelInstance = new BroadcastChannel(REPORT_SIDEBAR_CHANNEL);
  }
  return channelInstance;
}

export function postReportSidebarMessage(type, payload = {}) {
  const message = { type, payload, timestamp: Date.now() };

  const channel = getReportSidebarChannel();
  if (channel) {
    channel.postMessage(message);
  }

  if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
    try {
      window.opener.postMessage(message, window.location.origin);
    } catch (_) {
      // ignore cross-origin failures
    }
  }
}

export function subscribeReportSidebarMessages(handler) {
  const channel = getReportSidebarChannel();
  const onChannelMessage = (event) => handler(event.data);
  const onWindowMessage = (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type && Object.values(SYNC_MESSAGE).includes(event.data.type)) {
      handler(event.data);
    }
  };

  if (channel) channel.addEventListener('message', onChannelMessage);
  window.addEventListener('message', onWindowMessage);

  return () => {
    if (channel) channel.removeEventListener('message', onChannelMessage);
    window.removeEventListener('message', onWindowMessage);
  };
}

export function signalReportSidebarLogout() {
  const payload = { type: 'logout', timestamp: Date.now() };
  localStorage.setItem(REPORT_SIDEBAR_LOGOUT_KEY, JSON.stringify(payload));
  window.dispatchEvent(new StorageEvent('storage', {
    key: REPORT_SIDEBAR_LOGOUT_KEY,
    newValue: JSON.stringify(payload),
    url: window.location.href,
  }));
  postReportSidebarMessage(SYNC_MESSAGE.CONTEXT_CLEAR, { reason: 'logout' });
}
